import { ImportReview } from './ImportReview';
import {
  guessColumnMapping,
  rosterColumnLabels,
  parseCsv,
  type RosterField,
  splitFullName,
} from '@league/shared/csv';
import { useMemo, useRef, useState } from 'react';

import { Button, ErrorNote } from '../../components/ui';

const FIELDS: { key: RosterField; label: string; required: boolean }[] = [
  { key: 'firstName', label: 'First name', required: true },
  { key: 'lastName', label: 'Last name', required: false },
  { key: 'email', label: 'Email', required: true },
  { key: 'phone', label: 'Phone', required: false },
  { key: 'paceOverride', label: 'Playing pace', required: false },
];

const PACES = ['relaxed', 'steady', 'brisk', 'unknown'] as const;

/**
 * Importing a roster.
 *
 * Three things, in order, on one screen: give us the file, confirm which column is which, look at
 * what will happen. The director never leaves the page and never guesses — the preview shows real
 * names from their own file before anything is written.
 *
 * The column mapping is guessed and then shown, rather than guessed and used. Getting "Surname"
 * right automatically is a nice touch; getting it wrong silently is a roster full of people called
 * by their first name twice.
 */
export function ImportRoster({
  leagueId,
  seatsLeft: _seatsLeft,
  onImported,
  onCancel,
}: {
  leagueId: string;
  seatsLeft: number;
  onImported: () => void | Promise<unknown>;
  onCancel: () => void;
}) {
  const [firstRowIsHeader, setFirstRowIsHeader] = useState(false);
  const [text, setText] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const [mapping, setMapping] = useState<Partial<Record<RosterField, number>>>({});
  const [touched, setTouched] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const parsed = useMemo(() => (text.trim() ? parseCsv(text, firstRowIsHeader) : null), [text, firstRowIsHeader]);

  /** Re-guessed whenever a new file arrives, unless the director has already overridden it. */
  function loadText(next: string) {
    setText(next);
    const csv = next.trim() ? parseCsv(next) : null;
    if (csv && !touched) {
      const guessed = guessColumnMapping(csv.headers);
      const header = guessed.email !== undefined && guessed.firstName !== undefined;
      setFirstRowIsHeader(header);
      setMapping(header ? guessed : {});
    }
  }

  const splitNameColumn = mapping.firstName !== undefined && mapping.lastName === undefined;

  const rows = useMemo(() => {
    if (!parsed) return [];

    return parsed.rows.map((cells) => {
      const value = (field: RosterField) => {
        const index = mapping[field];
        return index === undefined ? '' : (cells[index] ?? '').trim();
      };

      const rawFirst = value('firstName');
      const { firstName, lastName } = splitNameColumn
        ? splitFullName(rawFirst)
        : { firstName: rawFirst, lastName: value('lastName') };

      const paceRaw = value('paceOverride').toLowerCase();
      const paceOverride = PACES.find(p => p === paceRaw) ?? (paceRaw === 'quick' ? 'brisk' : undefined) ?? (paceRaw === 'not sure yet' ? 'unknown' : undefined);

      return {
        firstName,
        lastName,
        email: value('email'),
        phone: value('phone') || null,
        ...(paceOverride ? { paceOverride } : {}),
        invalidPace: Boolean(paceRaw && !paceOverride),
      };
    });
  }, [parsed, mapping, splitNameColumn]);

  const missingRequired = FIELDS.filter((f) => f.required && mapping[f.key] === undefined);

  if (reviewing) return <ImportReview leagueId={leagueId} rows={rows.map(({invalidPace: _invalidPace,...row})=>row)} onImported={onImported} onBack={()=>setReviewing(false)}/>;

  return (
    <div className="flex flex-col gap-5 rounded-md border border-light bg-sand p-4">
      <div className="flex flex-col gap-2">
        <h3 className="font-ui text-[15px] font-medium">Import a roster</h3>
        <p className="text-[13px] text-ink-2">
          Export the sign-up list as CSV and choose the file, or paste it in. Nothing changes until
          you apply the reviewed changes.
        </p>
      </div>

      <div className="flex items-center gap-3">
        <input
          ref={fileInput}
          type="file"
          accept=".csv,.tsv,.txt,text/csv"
          className="hidden"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (file) loadText(await file.text());
          }}
        />
        <Button type="button" variant="secondary" onClick={() => fileInput.current?.click()}>
          Choose a file
        </Button>
        <span className="text-[13px] text-ink-3">or paste below</span>
      </div>

      <textarea
        value={text}
        onChange={(e) => loadText(e.target.value)}
        rows={5}
        spellCheck={false}
        placeholder={'First,Last,Email\nAda,Nakamura,ada@example.com'}
        className="w-full rounded-md border border-control-border bg-paper px-3 py-2.5 font-mono text-[13px] focus-visible:border-deep"
      />

      {parsed && parsed.headers.length > 0 ? (
        <>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={firstRowIsHeader} onChange={e => { setTouched(true); setFirstRowIsHeader(e.target.checked); }} />My list starts with headings like Name and Email.</label>
          <div className="flex flex-col gap-3">
            <div className="flex items-baseline gap-3">
              <h4 className="font-ui text-[13px] font-semibold uppercase tracking-[0.1em] text-ink-2">
                Which column is which
              </h4>
              <span className="tabular shrink-0 whitespace-nowrap text-[12px] text-ink-3">
                {parsed.rows.length} player{parsed.rows.length === 1 ? '' : 's'}
              </span>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {FIELDS.map((field) => (
                <label key={field.key} className="flex min-w-0 flex-col gap-1.5">
                  <span className="text-[13px] font-medium">
                    {field.label}
                    {field.required ? '' : ' (optional)'}
                  </span>
                  <select
                    value={mapping[field.key] ?? ''}
                    onChange={(e) => {
                      setTouched(true);
                      const next = { ...mapping };
                      if (e.target.value === '') delete next[field.key];
                      else next[field.key] = Number(e.target.value);
                      setMapping(next);
                    }}
                    className="h-10 w-full min-w-0 max-w-full rounded-md border border-control-border bg-paper px-2.5 text-[14px] focus-visible:border-deep"
                  >
                    <option value="">Not in this file</option>
                    {parsed.headers.map((header, index) => (
                      <option key={`${header}-${index}`} value={index}>
                        {rosterColumnLabels(parsed, firstRowIsHeader)[index]}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>

          </div>

          {rows.length > 0 ? (
            <div className="flex flex-col gap-2">
              <h4 className="font-ui text-[13px] font-semibold uppercase tracking-[0.1em] text-ink-2">
                Preview
              </h4>
              <ul className="flex flex-col divide-y divide-light rounded-md border border-light bg-paper">
                {rows.slice(0, 4).map((row, index) => (
                  <li key={index} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 px-4 py-2.5">
                    <span className="min-w-0 text-[14px]">
                      {row.firstName} {row.lastName}
                    </span>
                    {/* Wraps under the name on a narrow phone; a long address breaks rather than widening the page. */}
                    <span className="min-w-0 break-all text-[12px] text-ink-3">{row.email}</span>
                  </li>
                ))}
              </ul>
              {rows.length > 4 ? (
                <span className="tabular text-[12px] text-ink-3">
                  …and {rows.length - 4} more
                </span>
              ) : null}
            </div>
          ) : null}

          {missingRequired.length > 0 ? (
            <p className="text-[13px] text-ink-2">
              Still need: {missingRequired.map((f) => f.label.toLowerCase()).join(' and ')}.
            </p>
          ) : null}

        </>
      ) : null}

      {rows.some(row=>row.invalidPace) ? <ErrorNote>Check playing pace in rows {rows.flatMap((row,i)=>row.invalidPace ? [i+1] : []).join(', ')}. Use Relaxed, Steady, Quick, or Not sure yet. Experience values are not pace.</ErrorNote> : null}
      <div className="flex items-center gap-3">
        <Button
          type="button"
          disabled={rows.some(row=>row.invalidPace) || rows.length === 0 || missingRequired.length > 0}
          onClick={() => setReviewing(true)}
        >
          Review {rows.length} player{rows.length === 1 ? '' : 's'}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
